import * as Sentry from '@sentry/react';
import {
	browserTracingIntegration,
	captureConsoleIntegration,
	init as initSentryClient,
	replayIntegration,
	setTag
} from '@sentry/react';

import { isCI, isDev } from 'src/helpers/EnvironmentHelper';
import { isLocalhost } from 'src/helpers/UrlLocalhostHelper';
import { ITenant } from 'src/interfaces/Tenant';
import { IUser } from 'src/interfaces/User';
import { logger } from 'src/services/Logger';
import { beforeSendDropNoise } from 'src/services/SentryBeforeSend';
import {
	PRODUCT_NAME,
	SENTRY_PLACEHOLDER,
	SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
	SENTRY_REPLAY_SESSION_SAMPLE_RATE,
	SENTRY_TRACES_SAMPLE_RATE
} from 'src/services/SentryService.constants';

const isValidDsn = (dsn: string | undefined): boolean =>
	!!dsn && dsn !== SENTRY_PLACEHOLDER && !dsn.startsWith('x');

type TSentryServiceConfig = {
	dsn: string | undefined;
	environment: string | undefined;
	projectId: string | undefined;
	release: string;
};

class SentryService {
	private initialized = false;

	private readonly config: TSentryServiceConfig;

	constructor(config: TSentryServiceConfig) {
		this.config = config;
	}

	isEnabled(): boolean {
		return !isLocalhost && !isDev() && !isCI();
	}

	init(): void {
		try {
			if (this.initialized || !this.isEnabled() || !isValidDsn(this.config.dsn)) {
				return;
			}

			initSentryClient({
				dsn: this.config.dsn,
				integrations: [
					browserTracingIntegration(),
					replayIntegration({
						maskAllText: false,
						blockAllMedia: false
					}),
					captureConsoleIntegration({ levels: ['error'] })
				],
				tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
				replaysSessionSampleRate: SENTRY_REPLAY_SESSION_SAMPLE_RATE,
				replaysOnErrorSampleRate: SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
				release: this.config.release,
				environment: this.config.environment,
				ignoreErrors: ['Non-Error promise rejection captured'],
				beforeSend: beforeSendDropNoise
			});

			setTag('project-id', this.config.projectId);
			setTag('product-name', PRODUCT_NAME);
			this.initialized = true;
		} catch (error) {
			logger.error('Error initializing Sentry', error);
		}
	}

	initUser(user: IUser, tenant: ITenant): void {
		try {
			const userInfo = {
				email: user.email ?? '',
				role: user.role.name ?? '',
				roleType: user.role.type ?? ''
			};

			const tenantInfo = {
				accountPlan: tenant.accountPlan ?? '',
				companyName: tenant.companyName ?? '',
				id: tenant.id ?? '',
				name: tenant.name ?? ''
			};

			Sentry.setUser({
				id: user.id,
				username: `${user.name ?? ''} ${user.surname ?? ''}`,
				...tenantInfo,
				...userInfo
			});

			if (tenant.id) {
				setTag('tenant_id', tenant.id);
			}

			const tenantName = tenant.companyName ?? tenant.name;

			if (tenantName) {
				setTag('tenant_name', tenantName);
			}
		} catch (error) {
			logger.error('Error initializing Sentry user', error);
		}
	}
}

const sentryService = new SentryService({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.VITE_RUN_ENV,
	projectId: import.meta.env.VITE_SENTRY_PROJECT_ID,
	// eslint-disable-next-line no-undef
	release: `${UI_VERSION}`
});

export const initSentry = (): void => {
	sentryService.init();
};

export const initSentryUser = (user: IUser, tenant: ITenant): void => {
	sentryService.initUser(user, tenant);
};
